import { Check } from "lucide-react";
import { useLang } from "@/lib/i18n";

export const STEPS = [
  { n: 1, nn: "Datoar", en: "Dates" },
  { n: 2, nn: "Rom", en: "Rooms" },
  { n: 3, nn: "Tilpass", en: "Customise" },
  { n: 4, nn: "Gjesteinfo", en: "Details" },
];

/**
 * Checkout progress in the editorial language: numbered caps labels on a hairline.
 * Desktop: "01 Datoar ——— 02 Rom ———". Mobile: thin progress bars + "Steg x av 4".
 * `reachable` = highest step the guest may jump to.
 */
export const BookingStepper = ({ step, reachable, onGo, className = "" }) => {
  const { tx } = useLang();
  const current = STEPS.find((s) => s.n === step) || STEPS[0];
  const next = STEPS.find((s) => s.n === step + 1);
  return (
    <nav aria-label={tx("Bestillingssteg", "Booking steps")} data-testid="booking-stepper" className={className}>
      {/* mobile */}
      <div className="sticky top-16 z-30 -mx-5 border-b border-border bg-background/95 px-5 py-3 backdrop-blur sm:-mx-8 sm:px-8 lg:hidden" data-testid="booking-stepper-mobile">
        <div className="flex items-baseline justify-between gap-4 text-sm">
          <p className="font-medium text-foreground"><span className="text-muted-foreground tabular">{tx("Steg", "Step")} {step} {tx("av", "of")} 4 ·</span> {tx(current.nn, current.en)}</p>
          {next && <p className="truncate text-muted-foreground">{tx("Neste:", "Next:")} {tx(next.nn, next.en)}</p>}
        </div>
        <div className="mt-2.5 flex gap-1.5" aria-hidden>
          {STEPS.map((s) => <span key={s.n} className={`h-[2px] flex-1 transition-colors ${s.n <= step ? "bg-foreground" : "bg-border"}`} />)}
        </div>
      </div>

      {/* desktop */}
      <ol className="hidden items-center border-t border-border lg:flex">
        {STEPS.map((s, i) => {
          const done = s.n < step;
          const isCurrent = s.n === step;
          const can = s.n <= reachable && !isCurrent;
          return (
            <li key={s.n} className={`relative flex items-center ${i < STEPS.length - 1 ? "flex-1" : ""}`}>
              <button
                type="button"
                disabled={!can}
                onClick={() => can && onGo(s.n)}
                aria-current={isCurrent ? "step" : undefined}
                data-testid={`booking-stepper-step-${s.n}`}
                className={`group relative -mt-px flex items-center gap-3 border-t py-4 pr-6 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  isCurrent ? "border-foreground" : "border-transparent"
                } ${can ? "cursor-pointer" : "cursor-default"}`}
              >
                <span className={`index-num tnum transition-colors ${isCurrent ? "text-foreground" : done ? "text-foreground/70 group-hover:text-foreground" : "text-muted-foreground"}`}>
                  {done ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : String(s.n).padStart(2, "0")}
                </span>
                <span className={`text-[0.875rem] tracking-[0.01em] transition-colors ${isCurrent ? "font-medium text-foreground" : done ? "font-medium text-foreground/70 group-hover:text-foreground" : "text-muted-foreground"}`}>{tx(s.nn, s.en)}</span>
              </button>
              {i < STEPS.length - 1 && <span className="flex-1" aria-hidden />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
